/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './order-information-page.component.html';

import {lineItemCommons} from '../line-item/line-item-commons';
import {LineItemListSetupService} from '../line-item/line-item-list-setup.service';
import {creativeListCommons} from '../creative/creative-commons';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {AdnLogService} from '../../components/util/adn-log.service';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {OrderResource} from '../../components/api/resources/resource/order.resource';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {MarketplaceProductResource} from '../../components/api/resources/resource/marketplace-product.resource';
import {NetworkDataResource} from '../../components/api/resources/resource/network-data.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';


type OrderInformationTab = 'lineItems' | 'charts' | 'creatives';

function emptyCountList(): {totalCountText: string} {
  // Seed list count state before the list helper is attached.
  return {totalCountText: ''};
}

@Component({
  standalone: false,
  selector: 'adn-order-information-page',
  template
})
export class OrderInformationPageComponent implements DoCheck, OnChanges, OnDestroy {
  @Input() model: any;
  private _modelCommsDirect: any;
  private _isNewDirect: boolean | undefined;
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set modelComms(value: any) {
    this._modelCommsDirect = value;
  }

  get modelComms(): any {
    return this._modelCommsDirect;
  }

  @Input()
  set isNew(value: boolean) {
    this._isNewDirect = value;
  }

  get isNew(): boolean {
    return this._isNewDirect;
  }

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  effectiveIsNew = false;
  activeTab: OrderInformationTab = 'lineItems';
  chartsLoaded = false;

  isAxAdvertiser = false;
  chartCurrency: any;
  showCreatives = false;

  lineItems: any = emptyCountList();
  creatives: any = emptyCountList();
  lineItemsEmpty = true;
  lineItemsCount = 0;
  selLineItem: any;
  /** Snapshot — list-helper replaces lineItems.items mid-CD (NG0100). */
  displayLineItems: any[] = [];

  showLineItemForm = false;
  lineItem: any;
  objectiveFields: any = {};
  bidSpecificationFields: any = {};
  isSelfService = false;

  private lastModelId: string | undefined;
  private subscribedModelComms: any;
  private lineItemCommonsApi: any;
  private destroyed = false;

  constructor(
    @Inject(NetworkDataResource) private readonly NetworkData: any,
    @Inject(MarketplaceProductResource) private readonly MarketplaceProduct: any,
    private readonly adnLog: AdnLogService,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(OrderResource) private readonly Order: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService,
    private readonly $state: StateService,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly listService: AdnResourceListService,
    private readonly lineItemListSetup: LineItemListSetupService
  ) {
    this.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser();
    this.showCreatives = !!LocalUserPermissions.getSectionPerms().creatives;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      const wasNew = changes.isNew.previousValue !== undefined && !!changes.isNew.previousValue;
      this.effectiveIsNew = !!this.isNew;
      if (wasNew && !this.effectiveIsNew) {
        this.initLists();
      }
    }
    if (changes.model) {
      this.chartCurrency = _.get(this.model, ['objectives', 'BUDGET', 'currency']);
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId === this.lastModelId) {
      return;
    }
    this.lastModelId = modelId;
    // Macrotask — list fill setting totalCountText mid-CD is NG0100 on tab badges.
    setTimeout(() => {
      if (this.destroyed || this.lastModelId !== modelId) {
        return;
      }
      this.ngZone.run(() => this.initLists());
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: OrderInformationTab): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.chartsLoaded = true;
    }
  }

  loadExternalLineItem(): void {
    const viewType = this.LocalNetworkProfile.isCalt() && this.LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    this.$state.go('app.adv.line-items.new', {
      viewType,
      full: viewType === 2,
      orderId: this.model.id,
      orderName: this.model.name,
      advertiserId: _.get(this.model, ['advertiser', 'id']),
      advertiserName: _.get(this.model, ['advertiser', 'name'])
    });
  }

  loadLineItemForm(): void {
    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const networkNow = this.adnTimeShifter.getMomentInNetworkTimezone();
    let startDate = networkNow.clone().startOf('day').toDate();
    let endDate = networkNow.clone().add(networkDefaults.lineItemDaysApart, 'day').endOf('day').toDate();
    if (_.isDate(this.model.objectiveStartDate)) {
      startDate = this.model.objectiveStartDate;
    }
    if (_.isDate(this.model.objectiveEndDate)) {
      endDate = this.model.objectiveEndDate;
    }

    this.lineItem = this.LineItem.create({
      userState: this.isSelfService || this.isAxAdvertiser ? networkDefaults.marketplaceLineItemUserState || 'PROPOSED' : networkDefaults.lineItemUserState || 'APPROVED',
      order: this.model,
      startDate,
      endDate,
      modStartDate: startDate,
      modEndDate: endDate
    });

    this.lineItem.deliveryCurve = this.isAxAdvertiser
      ? this.LocalNetworkProfile.getDefaults().marketplaceDeliveryCurve
      : this.LocalNetworkProfile.getDefaults().deliveryCurve;

    if (this.isAxAdvertiser) {
      this.loadAxProductDefaults();
    }

    this.lineItemCommonsApi = lineItemCommons(this, this.translate, this.appLocale, this.LocalNetworkProfile, this.LocalUserPermissions, this.adnTimeShifter, 'lineItem');
    this.lineItemCommonsApi.setBidSpecificationFields();
    this.lineItemCommonsApi.setObjectivesAsString();
    this.lineItemCommonsApi.setObjectiveFields();
    this.objectiveFields = this.lineItemCommonsApi.objectiveFields || {};
    this.bidSpecificationFields = this.lineItemCommonsApi.bidSpecificationFields || {};

    const defaultTierId = this.LocalNetworkProfile.get('defaultTierId');
    if (defaultTierId) {
      this.lineItem.tier = {id: defaultTierId};
    }

    this.showLineItemForm = true;
  }

  lineItemObjectivesAsString(): string {
    return this.lineItemCommonsApi?.objectivesAsString?.() || '';
  }

  onBeforeLineItemSubmit(): void {
    this.lineItem.startDate = _.isDate(this.lineItem.modStartDate) ? this.lineItem.modStartDate : null;
    this.lineItem.endDate = _.isDate(this.lineItem.modEndDate) ? this.lineItem.modEndDate : null;
  }

  onLineItemSaved(lineItem: any): void {
    this.showLineItemForm = false;
    this.lineItems.addItemToList(lineItem);
    deferMarkForCheck(this.cdr, () => {
      this.snapshotLineItems();
    });
  }

  onLineItemFormCancelled(): void {
    this.showLineItemForm = false;
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', advertiserNavigationParams(this.model.advertiser));
  }

  newCreative(): void {
    this.$state.go('app.adv.creatives.new', advertiserNavigationParams(this.model.advertiser));
  }

  private initLists(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.lineItems = emptyCountList();
      this.creatives = emptyCountList();
      this.lineItemsEmpty = true;
      this.lineItemsCount = 0;
      this.displayLineItems = [];
      return;
    }

    this.lineItems = this.lineItemListSetup.createEmbeddedList({
      listId: 'fullLineItemsForOrder',
      moreParams: {order: this.model.id},
      view: 'ORDER_VIEW',
      onAfterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          this.snapshotLineItems();
        });
      }
    });
    if (this.lineItems.totalCountText === undefined || this.lineItems.totalCountText === null) {
      this.lineItems.totalCountText = '';
    }

    this.creatives = emptyCountList();
    this.listService.createBasicList(this.creatives, this.Creative, 'fullCreativesForOrder', {
      moreParams: {order: this.model.id},
      baseTable: 'creative',
      afterFill: () => {
        creativeListCommons(this.creatives, true, this.LocalNetworkProfile, this.$state);
      }
    });
    creativeListCommons(this.creatives, false, this.LocalNetworkProfile, this.$state);
  }

  private loadAxProductDefaults(): void {
    let teamToProduct: any;
    const defaultsQuery = this.NetworkData.get({filterByValue: 'defaultMarketplaceProducts', filterBy: 'type'}).$promise.then((page: any) => {
      if (_.get(page, ['results', 'length'])) {
        const entry = page.results[0];
        try {
          teamToProduct = JSON.parse(entry.data.teamToProduct);
        } catch {
          this.adnLog.warn('Found a problem find teams to products for defaults');
        }
      }
    });

    Promise.resolve(defaultsQuery).then(() => {
      this.MarketplaceProduct.query({excludeInvalid: true}).$promise.then((page: any) => {
        this.ngZone.run(() => {
          const marketplaceProducts = page.results;
          if (teamToProduct) {
            const matchedMpId = _.find(teamToProduct, (val: any, key: string) => {
              const isTeam = this.LocalUserPermissions.has('AX_ADVERTISER', key);
              return isTeam && _.find(marketplaceProducts, (mp: any) => mp.id === teamToProduct[key]);
            });
            const matchedMp = _.find(marketplaceProducts, ['id', matchedMpId]);
            if (matchedMp) {
              this.lineItem.axProduct = matchedMp;
              _.set(this.lineItem, 'targeting', matchedMp.targeting);
              this.lineItem.rateLimits = matchedMp.rateLimits;
            }
          }
        });
      });
    });
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any) => {
      if (type || !data?.name) {
        return;
      }
      this.ngZone.run(() => {
        if (data.name !== undefined && this.model) {
          this.model.name = data.name;
        }
        this.chartCurrency = _.get(data, ['objectives', 'BUDGET', 'currency']) || this.chartCurrency;
        if (this.effectiveIsNew) {
          this.effectiveIsNew = false;
          this.initLists();
        } else {
          this.refreshEmbeddedLists();
        }
      });
    });
  }

  private snapshotLineItems(): void {
    this.displayLineItems = (this.lineItems.items || []).slice();
    this.lineItemsEmpty = this.displayLineItems.length === 0;
    this.lineItemsCount = this.displayLineItems.length;
    this.selLineItem = this.displayLineItems[0];
  }

  private refreshEmbeddedLists(): void {
    if (_.isFunction(this.lineItems?.makeQuery)) {
      this.lineItems.makeQuery();
    }
    if (_.isFunction(this.creatives?.makeQuery)) {
      this.creatives.makeQuery();
    }
  }
}
